import { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, Network } from 'lucide-react';
import type { ActiveClientSummary, ActiveNetworkSummary } from '@just-frotas/shared';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  SearchableSelect,
  type SearchableSelectOption,
} from '@/components/SearchableSelect';
import { activeClientService } from '@/services/active-client';
import { authService } from '@/services/auth';
import {
  podeTrocarCliente,
  podeTrocarRede,
  usarBuscaServidorClientes,
  usarBuscaServidorRedes,
} from '@/lib/contexto-navegacao';
import { useAuth } from '@/contexts/AuthContext';
import { useActiveClient } from '@/contexts/ActiveClientContext';
import { useNavigation } from '@/contexts/NavigationContext';
import { useProfile } from '@/contexts/ProfileContext';
import { ApiError } from '@/lib/api-client';
import { formatCnpj } from '@/lib/format';

// Modal de troca de contexto de navegacao. Sections (cliente / rede)
// aparecem condicionalmente baseado no perfil ativo + escopo do user
// (decisao CEO 2026-05-04):
//   Frotista: SO section Cliente.
//   Posto:    SO section Rede.
//   Admin:    nao deveria abrir (badge oculto), mas defensivo: ambas
//             escondidas se atingir esse caminho.
// Backend ja faz filtragem por master quando aplicavel (cluster master
// de cliente ve soh clientes do cluster; rede master ve soh redes do
// cluster).

interface Props {
  open: boolean;
  onClose: () => void;
}

function toClienteOption(c: ActiveClientSummary): SearchableSelectOption {
  return {
    value: c.id,
    label: c.nome,
    hint: c.cnpj ? (formatCnpj(c.cnpj) ?? c.cnpj) : undefined,
  };
}

function toRedeOption(r: ActiveNetworkSummary): SearchableSelectOption {
  return {
    value: String(r.id),
    label: r.nome,
    hint: r.cnpj ? (formatCnpj(r.cnpj) ?? r.cnpj) : undefined,
  };
}

export function TrocaContextoModal({ open, onClose }: Props) {
  const { user } = useAuth();
  const { activeProfile } = useProfile();
  const { clientes, activeClientUuid, switchClient } = useActiveClient();
  const { redes, activeNetworkId, switchNetwork } = useNavigation();

  const [selectedCliente, setSelectedCliente] = useState<string | null>(activeClientUuid);
  const [selectedRede, setSelectedRede] = useState<string | null>(
    activeNetworkId !== null ? String(activeNetworkId) : null,
  );
  const [submitting, setSubmitting] = useState(false);

  // Resync com state externo cada vez que abre.
  useEffect(() => {
    if (open) {
      setSelectedCliente(activeClientUuid);
      setSelectedRede(activeNetworkId !== null ? String(activeNetworkId) : null);
    }
  }, [open, activeClientUuid, activeNetworkId]);

  // Opcoes de cliente da lista ja carregada (ate 200 do backend).
  const clienteOptions = useMemo<SearchableSelectOption[]>(
    () => clientes.map(toClienteOption),
    [clientes],
  );

  // Busca server-side (?busca=) quando a lista carregada pode estar truncada
  // (admin ve todos os clientes do sistema). Texto vazio reaproveita a lista
  // ja carregada, sem request extra. Debounce de 300ms e do SearchableSelect.
  const buscarClientes = useCallback(
    async (q: string): Promise<SearchableSelectOption[]> => {
      const termo = q.trim();
      if (!termo) return clienteOptions;
      const resp = await activeClientService.list(termo.slice(0, 120));
      return resp.clientes.map(toClienteOption);
    },
    [clienteOptions],
  );

  // Opcao do cliente selecionado para o trigger no modo server-side (antes
  // do primeiro onSearch o SearchableSelect nao conhece o label). Memo: o
  // SearchableSelect sincroniza cache por referencia de initialOption.
  const ctxCliente = user?.activeContext?.cliente ?? null;
  const clienteAtivoOption = useMemo<SearchableSelectOption | null>(() => {
    const daLista = clienteOptions.find((o) => o.value === selectedCliente);
    if (daLista) return daLista;
    if (ctxCliente && ctxCliente.uuid === selectedCliente) {
      return { value: ctxCliente.uuid, label: ctxCliente.nome };
    }
    return null;
  }, [clienteOptions, selectedCliente, ctxCliente]);

  // Mesma logica de cliente, para redes (admin enxerga todas as redes).
  const redeOptions = useMemo<SearchableSelectOption[]>(() => redes.map(toRedeOption), [redes]);

  const buscarRedes = useCallback(
    async (q: string): Promise<SearchableSelectOption[]> => {
      const termo = q.trim();
      if (!termo) return redeOptions;
      const resp = await authService.listRedesAcessiveis(termo.slice(0, 80));
      return resp.redes.map(toRedeOption);
    },
    [redeOptions],
  );

  const ctxRede = user?.activeContext?.rede ?? null;
  const redeAtivaOption = useMemo<SearchableSelectOption | null>(() => {
    const daLista = redeOptions.find((o) => o.value === selectedRede);
    if (daLista) return daLista;
    if (ctxRede && String(ctxRede.id) === selectedRede) {
      return { value: String(ctxRede.id), label: ctxRede.nome };
    }
    return null;
  }, [redeOptions, selectedRede, ctxRede]);

  if (!user) return null;

  const podeTrocarClienteAtual = podeTrocarCliente(user, clientes.length);
  const podeTrocarRedeAtual = podeTrocarRede(user);
  const buscaServidor = usarBuscaServidorClientes(user, clientes.length);
  const buscaServidorRedes = usarBuscaServidorRedes(user, redes.length);

  // Visibilidade das sections: filtrado por perfil ativo (decisao CEO
  // 2026-05-04). Frotista ve so Cliente, Posto ve so Rede. Vinculo do
  // user ainda eh checado dentro de cada perfil (sem vinculo = section
  // nao aparece mesmo no perfil correto).
  const temVinculoCliente = podeTrocarClienteAtual || !!user.clientUuid;
  const temVinculoRede = podeTrocarRedeAtual || !!user.networkId;
  const showCliente = temVinculoCliente && activeProfile === 'frotista';
  const showRede = temVinculoRede && activeProfile === 'posto';

  // Pode trocar (vs apenas visualizar) - sections somem se cliente/rede
  // regular (so ve o atual sem opcao de troca).
  const podeTrocarClienteNaTela = showCliente && podeTrocarClienteAtual;
  const podeTrocarRedeNaTela = showRede && podeTrocarRedeAtual;

  async function handleApply() {
    setSubmitting(true);
    try {
      const tasks: Promise<void>[] = [];
      if (podeTrocarClienteNaTela && selectedCliente && selectedCliente !== activeClientUuid) {
        tasks.push(switchClient(selectedCliente));
      }
      if (podeTrocarRedeNaTela && selectedRede !== null) {
        const next = Number(selectedRede);
        if (next !== activeNetworkId) {
          tasks.push(switchNetwork(next));
        }
      }
      if (tasks.length === 0) {
        toast.info('Nada para alterar.');
        onClose();
        return;
      }
      await Promise.all(tasks);
      toast.success('Contexto atualizado.');
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : 'Erro ao trocar contexto.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Trocar contexto de navegação</DialogTitle>
          <DialogDescription>
            Escolha o cliente e/ou a rede que deseja usar para navegar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {showCliente ? (
            <section className="space-y-2">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-text-muted font-semibold">
                <Building2 className="w-3.5 h-3.5" />
                Cliente
              </div>
              {podeTrocarClienteNaTela ? (
                <div>
                  <Label htmlFor="tc-cliente" className="sr-only">
                    Cliente
                  </Label>
                  <SearchableSelect
                    value={selectedCliente}
                    onChange={(v) => setSelectedCliente(v)}
                    {...(buscaServidor
                      ? { onSearch: buscarClientes, initialOption: clienteAtivoOption }
                      : { options: clienteOptions })}
                    placeholder="Selecionar cliente..."
                    searchPlaceholder="Buscar cliente..."
                    emptyMessage="Nenhum cliente acessível."
                    disablePortal
                  />
                </div>
              ) : (
                // Cliente regular: mostra read-only o cliente vinculado.
                <p className="text-sm text-text-strong px-3 py-2 bg-surface-soft rounded-md border border-border">
                  {user.activeContext?.cliente?.nome ?? '(cliente vinculado)'}
                </p>
              )}
            </section>
          ) : null}

          {showRede ? (
            <section className="space-y-2">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-text-muted font-semibold">
                <Network className="w-3.5 h-3.5" />
                Rede
              </div>
              {podeTrocarRedeNaTela ? (
                <div>
                  <Label htmlFor="tc-rede" className="sr-only">
                    Rede
                  </Label>
                  <SearchableSelect
                    value={selectedRede}
                    onChange={(v) => setSelectedRede(v)}
                    {...(buscaServidorRedes
                      ? { onSearch: buscarRedes, initialOption: redeAtivaOption }
                      : { options: redeOptions })}
                    placeholder="Selecionar rede..."
                    searchPlaceholder="Buscar rede..."
                    emptyMessage="Nenhuma rede acessível."
                    disablePortal
                  />
                </div>
              ) : (
                <p className="text-sm text-text-strong px-3 py-2 bg-surface-soft rounded-md border border-border">
                  {user.activeContext?.rede?.nome ?? '(rede vinculada)'}
                </p>
              )}
            </section>
          ) : null}

          {!showCliente && !showRede ? (
            <p className="text-sm text-text-muted text-center py-6">
              Você não tem contexto de navegação para trocar.
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleApply}
            disabled={submitting || (!podeTrocarClienteNaTela && !podeTrocarRedeNaTela)}
          >
            {submitting ? 'Aplicando...' : 'Aplicar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
